import React from 'react';

import { MAIN_THEME } from '@styles';
import {
  addZeroesAndSeparatevalue,
  trimTrailingZeros,
  valueCommaSeparated
} from '@utils/formatting-utils';
import { isValidNumber } from '@utils/validationUtils';

import { InformationDataType } from '.';

import DateService from 'src/core/services/DateService';
import PrecisionService from 'src/core/services/PrecisionService';
import { makeStyles } from 'tss-react/mui';

interface IInformationBoxCellProps {
  type?: InformationDataType;
  value: string;
  currency: string;
  cdnUrl: string;
}

const useStyles = makeStyles()(() => ({
  text: {
    fontSize: MAIN_THEME.fontSize.xs,
    color: MAIN_THEME.color.black,
    display: 'inline-block',
    textAlign: 'right',
    lineHeight: '15px',
    width: '100%'
  },
  figure: {
    width: '15px',
    height: '15px',
    backgroundSize: '100%',
    backgroundPosition: 'center',
    float: 'right',
    margin: 0,
    backgroundRepeat: 'no-repeat'
  },
  statusCircle: {
    width: '15px',
    height: '15px',
    borderRadius: '50%'
  }
}));

const InformationBoxBodyCell: React.FC<IInformationBoxCellProps> = ({
  type = InformationDataType.Text,
  value,
  cdnUrl,
  currency = '$'
}) => {
  const { classes } = useStyles();

  const isZeroValue = (v: any) => v === 0 || v === '0';
  const hasNonEmptyValue = (v: any) =>
    v !== null && v !== undefined && v !== '';

  const renderCell = () => {
    switch (type) {
      case InformationDataType.Integer: {
        return (
          <div className={classes.text}>
            {isValidNumber(value) ? Number(value) : 'NA'}
          </div>
        );
      }
      case InformationDataType.Currency: {
        if (isValidNumber(value)) {
          if (isZeroValue(value)) {
            return <div className={classes.text}>{`${currency} 0`}</div>;
          }

          if (hasNonEmptyValue(value)) {
            const numberValue = Number(value);
            if (numberValue >= 0) {
              return (
                <div className={classes.text}>
                  {`${currency} ${addZeroesAndSeparatevalue(numberValue)}`}
                </div>
              );
            }

            return (
              <div className={classes.text}>
                {`${currency} (${addZeroesAndSeparatevalue(
                  Math.abs(numberValue)
                )})`}
              </div>
            );
          }

          return <div className={classes.text}>NA</div>;
        }

        return <div className={classes.text}>NA</div>;
      }
      case InformationDataType.RoundedCurrency: {
        if (isValidNumber(value)) {
          if (isZeroValue(value)) {
            return <div className={classes.text}>{`${currency} 0`}</div>;
          }

          if (hasNonEmptyValue(value)) {
            const numberValue = Number(value);
            if (numberValue >= 0) {
              return (
                <div className={classes.text}>
                  {`${currency} ${trimTrailingZeros(
                    valueCommaSeparated(numberValue),
                    3,
                    true
                  )}`}
                </div>
              );
            }

            return (
              <div className={classes.text}>
                {`${currency} (${trimTrailingZeros(
                  valueCommaSeparated(Math.abs(numberValue)),
                  3,
                  true
                )})`}
              </div>
            );
          }

          return <div className={classes.text}>NA</div>;
        }

        const numberValue = Number(value);

        if (numberValue >= 0) {
          return (
            <div className={classes.text}>
              {`${currency} ${trimTrailingZeros(
                valueCommaSeparated(numberValue),
                3,
                true
              )}`}
            </div>
          );
        }

        return (
          <div className={classes.text}>
            {`${currency} (${trimTrailingZeros(
              valueCommaSeparated(Math.abs(numberValue)),
              3,
              true
            )})`}
          </div>
        );
      }

      case InformationDataType.Percentage: {
        return (
          <div className={classes.text}>
            {isValidNumber(value) ? `${valueCommaSeparated(value)}%` : 'NA'}
          </div>
        );
      }
      case InformationDataType.RoundedPercentage: {
        return (
          <div className={classes.text}>
            {isValidNumber(value)
              ? `${trimTrailingZeros(valueCommaSeparated(value), 3, true)}%`
              : 'NA'}
          </div>
        );
      }
      case InformationDataType.Rate: {
        return (
          <div className={classes.text}>
            {isValidNumber(value)
              ? `${valueCommaSeparated(
                  PrecisionService.multiplyBy100(Number(value))
                )}%`
              : 'NA'}
          </div>
        );
      }
      case InformationDataType.Date:
        return (
          <div className={classes.text}>{DateService.formatDate(value)}</div>
        );
      case InformationDataType.DateAndTime:
        return (
          <div className={classes.text}>
            {DateService.formatDateTime(value)}
          </div>
        );
      case InformationDataType.YesNo:
        return (
          <div className={classes.text}>
            <figure
              className={classes.figure}
              style={{
                backgroundImage:
                  value === 'Yes'
                    ? `url(${cdnUrl}/icons/tick-primary.svg)`
                    : `url(${cdnUrl}/icons/x-secondary.svg)`,
                backgroundSize: value === 'Yes' ? '100%' : '80%'
              }}
            />
          </div>
        );
      case InformationDataType.Status: {
        return (
          <div
            style={{ backgroundColor: value }}
            className={classes.statusCircle}
          />
        );
      }
      case InformationDataType.SummaryList: {
        return <span>{value}</span>;
      }
      default:
        return (
          <div className={classes.text}>
            {value || isZeroValue(value) ? value : 'NA'}
          </div>
        );
    }
  };

  return renderCell();
};

export default InformationBoxBodyCell;
